import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import moment, { Moment } from 'moment'
import styled from 'styled-components'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Heading from 'components/Luxkit/Typography/Heading'
import { countOccupants, countOccupantsForFlights, formatOccupantsAsGuests, formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import MultipleRoomsBookingOption from './MultipleRoomsBookingOption'
import useToggle from 'hooks/useToggle'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Breakpoint from 'components/Common/Breakpoint'
import Caption from 'components/Luxkit/Typography/Caption'
import { DMY_CASUAL_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import MultipleRoomsBookingRoomsModal from './MultipleRoomsBookingRoomsModal'
import MultipleRoomsBookingContentFooter from './MultipleRoomsBookingContentFooter'
import { useInView } from 'react-intersection-observer'
import FormatCurrency from 'components/Common/FormatCurrency'
import {
  FlightsInfoByRoom,
  getActivePackagesByRoom,
  getAvailablePackagesByRoom,
  getCalendarsToBeFetchedByRoom,
  getCheapestPackagesByRoom,
  getDurationPackageOptionsByRoom,
  getFlightsInfoByRoom,
  isLoadingByRoom,
  LoadingStatusByRoom,
  PackageOptionViewByRoom,
  PackagesOptionsViewsByRoom,
} from 'selectors/multipleRoomBookingSelectors'

import { useAppDispatch } from 'hooks/reduxHooks'
import { generateMultipleRoomCheckoutItems } from 'lib/checkout/accommodation/cart'
import { addToCheckoutCart, setRoomsToBeConnected } from 'actions/CheckoutActions'
import { fireInteractionEvent } from 'api/googleTagManager'
import { getPlural, pluralizeToString } from 'lib/pluralize'
import BnblModal from '../BnblModal/BnblModal'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import { countBy, groupBy, sum } from 'lib/arrayUtils'
import { accountAccessShowLogin } from 'actions/UiActions'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms } from '../BnblModal/utils'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { arrayToStringListFormat } from 'lib/stringUtils'
import { calendarAction } from 'analytics/eventDefinitions'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { isRoomsInvalid } from 'lib/searchUtils'
import RequestConnectingRoomsInput from './RequestConnectingRoomsInput'
import { mergeObjects } from 'lib/objectUtils'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import TextLink from 'components/Luxkit/TextLink'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { FlightViewTypes } from 'constants/flight'

const PackageOptionsContainer = styled(LayoutContainer)`
  padding: 0;
  position: relative;
`

const StyledHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  margin-top: ${rem(24)};
  padding: 0 ${rem(16)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    gap: 0;
    padding: 0;
  }
`
const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }
`

const FiltersSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(8)};
  align-items: center;
  justify-content: space-between;

  ${mediaQueryUp.tablet} {
    justify-content: end;
  }
`

const FooterContainer = styled.div`
  margin-top: ${rem(24)};
  padding: 0 ${rem(16)};

  ${mediaQueryUp.desktop} {
    padding: 0;
  }
`

const StyledWhiteBlockContainer = styled.div`
  background-color: #FFF;
  border: 1px solid ${props => props.theme.palette.neutral.five};
  padding: ${rem(16)};
  margin-top: ${rem(24)};
`

const StyledPackageContainer = styled(StyledWhiteBlockContainer)`
  padding: 0;
`

const InlineContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledRoomPackageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.five};
`

const StyledRemoveButton = styled(TextButton)`
  padding: 0;
  margin: -${rem(4)} 0;
`

const StyledRoomTitle = styled(BodyText)`
  font-family: ${props => props.theme.font.secondary.family};
`

const StyledRoomOccupancyString = styled(BodyText)`
  margin-left: ${rem(8)};
`

const StyledActionFooterButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueryUp.desktop} {
    justify-content: flex-end;
    margin-left: auto;
  }
`

const BNBLBox = styled.div`
  text-align: center;
`

const StyledOfferBookButton = styled(TextButton)`
  margin-left: ${rem(24)};
`

const StyledFilterButton = styled(FilterChip)`
  margin-left: ${rem(8)};
`

const StyledInlineGapContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const NoPackagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(16)};
  gap: ${rem(8)};
`

const NoPackagesTextBox = styled.div`
  display: flex;
  align-items: center;
  padding: ${rem(9)};
  background-color: ${props => props.theme.palette.neutral.seven};
`

interface Props {
  rooms?: App.RoomOccupants[];
  offer: App.Offer;
  checkIn?: Moment;
  duration?: number;
  activeFilter?: OfferPageFilterTypes;
  cheapestPackagesByRoom: PackageOptionViewByRoom;
  activePackagesByRoom: PackageOptionViewByRoom;
  isLoadingByRoom: LoadingStatusByRoom;
  calendarsToBeFetchedByRoom: LoadingStatusByRoom;
  flightsInfoByRoom: FlightsInfoByRoom;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  fallbackPackagesByRoom: PackagesOptionsViewsByRoom;
  account?: App.AuthAccount;
  isLoggedIn?: boolean;
  baseBnblPackageViews: OfferPackageOptionView[];
  roomTypesByRoom: { [roomId: string]: string };
  currencyCode: string;
  isMultiRoomConnected: boolean;
  airport?: App.Airport;
}

function MultipleRoomsBookingContent(props: Props) {
  const {
    rooms,
    offer,
    checkIn,
    duration,
    activeFilter,
    cheapestPackagesByRoom,
    activePackagesByRoom,
    isLoadingByRoom,
    calendarsToBeFetchedByRoom,
    flightsInfoByRoom,
    availablePackagesByRoom,
    fallbackPackagesByRoom,
    isLoggedIn,
    baseBnblPackageViews,
    roomTypesByRoom,
    currencyCode,
    isMultiRoomConnected,
    airport,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const [reviewPageRef, reviewPageInView] = useInView()
  const [footerRef, footerInView] = useInView()

  const isFlightsForAllRoomsSelected = useMemo(() => Object.values(flightsInfoByRoom).every(flightsInfo => flightsInfo.isFlightsSelected), [flightsInfoByRoom])
  const isFlightsForAllRoomsAvailable = useMemo(() => Object.values(flightsInfoByRoom).every(flightsInfo => flightsInfo.isFlightsAvailable), [flightsInfoByRoom])

  const [closeProximity, onToggleProximity] = useToggle(Boolean(isMultiRoomConnected))
  const [buyNowBookLater, _, openBNBLModal, closeBNBLModal] = useToggle()
  const [isOpenRoomsModal, , openRoomsModal, closeRoomsModal] = useToggle()
  const [changedRoom, setChangedRoom] = useState<App.RoomOccupants>(null)

  const allRooms = countOccupants(rooms)
  const showGuestsLabel = allRooms.children || allRooms.infants
  const occupantsString = showGuestsLabel ? `${formatOccupantsAsGuests(allRooms)} • ${formatOccupantsShort(allRooms)}` : formatOccupantsShort(allRooms)
  const changedRoomIndex = useMemo(() => rooms.findIndex(room => room.roomId === changedRoom?.roomId), [rooms, changedRoom])
  const hasNoFlightPrices = useMemo(() => !!Object.values(flightsInfoByRoom).find(pkg => !pkg.flightPrice), [flightsInfoByRoom])
  const { showTaxExclusivePrices } = useContext(GeoContext)

  const loginListener = useReduxStateListener<{ loggedIn: boolean }>((state) => {
    if (isLoggedInSelector(state)) {
      return { loggedIn: true }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  const onOpenRoomsModal = useCallback((room) => {
    setChangedRoom(room)

    openRoomsModal()
  }, [openRoomsModal])

  const onSelectPackage = useCallback((packageOption: App.Package) => {
    offerPageDispatch({
      type: OfferPageActions.selectRoomPackage,
      packageOption,
      room: changedRoom,
    })

    closeRoomsModal()
  }, [offerPageDispatch, changedRoom, closeRoomsModal])

  const onToggleFlightsForAllRooms = useCallback(() => {
    offerPageDispatch({
      type: OfferPageActions.toggleFlightsForAllRooms,
      isFlightsSelected: !isFlightsForAllRoomsSelected,
    })
  }, [offerPageDispatch, isFlightsForAllRoomsSelected])

  const lastValidPackagesIdsByRoom = useRef<{ [roomId: string]: string }>({})
  const previousPackagesByRoom = useRef<PackageOptionViewByRoom>({})
  const previousDuration = useRef<number>()
  const selectionAmountByPackage = useRef<Map<string, number>>(new Map<string, number>())

  const packagesByRoom = useMemo(() => {
    const selectedPackages = { ...activePackagesByRoom }
    Object
      .entries(selectedPackages)
      // filters only rooms without package
      .filter(([, selectedPackage]) => !selectedPackage)
      .forEach(([roomId]) => {
        const isThisRoomLoading = isLoadingByRoom[roomId] || calendarsToBeFetchedByRoom[roomId]
        // If this room has a "loading" state, we select
        // the first available package for it, only for the
        // component to show the loading indicator
        selectedPackages[roomId] = isThisRoomLoading ? fallbackPackagesByRoom[roomId][0] : undefined
      })

    return selectedPackages
  }, [activePackagesByRoom, isLoadingByRoom, fallbackPackagesByRoom, calendarsToBeFetchedByRoom])

  const isCheapestPackagesActiveByRoom = useMemo(() => {
    const cheapestByRoom = {}
    Object
      .entries(packagesByRoom)
      .forEach(([roomId, packageOption]) => {
        cheapestByRoom[roomId] = packageOption &&
          cheapestPackagesByRoom[roomId] &&
          cheapestPackagesByRoom[roomId].package.uniqueKey === packageOption.package.uniqueKey
      })

    return cheapestByRoom
  }, [packagesByRoom, cheapestPackagesByRoom])

  const totalRoomsPrice = useMemo(() => (
    Object.keys(packagesByRoom).reduce((acc, roomId) => {
      if (!packagesByRoom[roomId]) return acc

      const { hotelPrice, flightPrice, propertyFees } = packagesByRoom[roomId]
      const { isFlightsSelected } = flightsInfoByRoom[roomId]

      const totalHotelPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees
      const totalRoomPrice = isFlightsSelected ? totalHotelPrice + flightPrice : totalHotelPrice

      return acc + totalRoomPrice
    }, 0)
  ), [packagesByRoom, flightsInfoByRoom, showTaxExclusivePrices])

  const isBookNowBtnDisabled = useMemo(() => (
    Object.values(isLoadingByRoom).some(Boolean) ||
    Object.values(calendarsToBeFetchedByRoom).some(Boolean) ||
    Object.values(packagesByRoom).some(pkg => !pkg)
  ), [isLoadingByRoom, calendarsToBeFetchedByRoom, packagesByRoom])

  const onOpenBnblModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const openAndInitaliseBnblModal = () => {
      e.preventDefault()
      openBNBLModal()

      offerPageDispatch({
        type: OfferPageActions.toggleFlightsForAllRooms,
        isFlightsSelected: false,
      })

      const selectedPackages = Object.values(packagesByRoom)
      const groupedPackagesRecords = groupBy(selectedPackages, pkg => pkg.package.uniqueKey)
      const packages = [...groupedPackagesRecords.entries()].map(groupedPackage => {
        const basePackage = baseBnblPackageViews.find(packageView => packageView.package.uniqueKey === groupedPackage[0])
        const numberOfRooms = groupedPackage[1].length

        return {
          packageView: getBnblPackageViewByNumberOfRooms(basePackage, numberOfRooms),
          numberOfRooms,
        }
      })

      dispatch(initialiseBnblOfferModal(packages, offer.id, false))
    }

    if (!isLoggedIn) {
      e.preventDefault()
      dispatch(accountAccessShowLogin())

      loginListener.awaitResult().then(({ loggedIn }) => {
        if (!loggedIn) return
        openAndInitaliseBnblModal()
      })
    }

    if (isLoggedIn) {
      openAndInitaliseBnblModal()
    }
  }, [openBNBLModal, offerPageDispatch, dispatch, offer.id, packagesByRoom, baseBnblPackageViews, isLoggedIn, loginListener])

  const onRemoveRoom = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = Number(e.currentTarget.dataset.idx)
    const newRooms = rooms?.filter((room, idx) => idx !== index)

    setChangedRoom(null)

    offerPageDispatch({
      type: OfferPageActions.setRooms,
      rooms: newRooms,
    })
  }, [rooms, offerPageDispatch])

  const proceedToCheckout = useCallback(() => {
    const checkOut = checkIn.clone().add(duration, 'days')
    const dates = { checkIn: checkIn.format(ISO_DATE_FORMAT), checkOut: checkOut.format(ISO_DATE_FORMAT) }

    const accommodationItems = generateMultipleRoomCheckoutItems(packagesByRoom, offer, dates, rooms, currencyCode)
    // For now our only option is to connect all rooms as a single group or none
    const roomsToBeConnected = closeProximity ? [accommodationItems.map(item => item.itemId)] : []
    const flightItems: Array<App.Checkout.FlightItem> = []
    const roomsWithFlights = rooms?.filter(room => flightsInfoByRoom[room.roomId]?.isFlightsSelected) ?? []
    if (roomsWithFlights.length > 0 && airport) {
      const flightOccupants = countOccupantsForFlights(roomsWithFlights)
      flightItems.push(generateFlightCheckoutItem({
        originAirportCode: airport.code,
        destinationAirportCode: offer.flightDestinationPort,
        bundledItemIds: accommodationItems.map(item => item.itemId),
        occupants: flightOccupants,
        fareType: 'return',
        viewType: FlightViewTypes.TWO_ONE_WAYS,
        passengers: generatePassengersFromOccupants(flightOccupants),
        quotedFare: sum(roomsWithFlights, room => flightsInfoByRoom[room.roomId]?.flightPrice ?? 0),
      }))
    }
    dispatch(addToCheckoutCart([...accommodationItems, ...flightItems]))
    dispatch(setRoomsToBeConnected(roomsToBeConnected))

    if (closeProximity) {
      fireInteractionEvent({
        category: 'multiple-rooms',
        action: 'request-connecting',
        label: 'toggle',
      })
    }

    dispatch(navigateToCheckout())
  }, [checkIn, duration, packagesByRoom, offer, rooms, currencyCode, closeProximity, airport, dispatch, flightsInfoByRoom])

  const feedbackMessage = useMemo(() => {
    return ({
      error: (title: string, message: string = '') => showSnackbar(message, 'critical', { heading: title }),
      success: (title: string, message: string = '') => showSnackbar(message, 'success', { heading: title }),
    })
  }, [])

  const getChangedRoomsFeedbackMessage = useCallback(() => {
    const resultMessage = {
      error: null,
      success: null,
    }

    const changedRoomsIds =
      Object
        .keys(packagesByRoom)
        .filter(roomId => roomId in previousPackagesByRoom.current && packagesByRoom[roomId]?.package.uniqueKey !== previousPackagesByRoom.current[roomId]?.package.uniqueKey)

    if (changedRoomsIds.length) {
      const roomsIdsWithoutPackage = changedRoomsIds.filter(roomId => !packagesByRoom[roomId])
      const roomsIdsWithPackage = changedRoomsIds.filter(roomId => packagesByRoom[roomId])

      if (roomsIdsWithoutPackage.length) {
        const roomsToString = roomsIdsWithoutPackage.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
        resultMessage.error = {
          title: `No available packages for ${arrayToStringListFormat(roomsToString, 'and')}`,
          message: 'Please revise number of guests or adjust selected dates.',
        }
      } else {
        const didDurationChanged = previousDuration.current != duration
        const roomsWithDifferentRoomType = roomsIdsWithPackage.filter(roomId => roomTypesByRoom[roomId] && roomTypesByRoom[roomId] != packagesByRoom[roomId].package.roomType.id)

        if (didDurationChanged && roomsWithDifferentRoomType.length) {
          const roomsToString = roomsWithDifferentRoomType.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
          resultMessage.error = {
            title: `The room type for ${arrayToStringListFormat(roomsToString, 'and')} is unavailable for selected duration`,
            message: 'We have selected the lowest price package available',
          }
        } else {
          const roomsToString = roomsIdsWithPackage.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
          resultMessage.success = {
            title: `${getPlural('Package', roomsToString.length)} for ${arrayToStringListFormat(roomsToString, 'and')} has been updated`,
          }
        }
      }
    }

    return resultMessage
  }, [packagesByRoom, previousPackagesByRoom, rooms, previousDuration, duration, roomTypesByRoom])

  const getNewRoomsFeedbackMessage = useCallback(() => {
    const resultMessage = {
      error: null,
      success: null,
    }

    const previousRoomsIds = new Set(Object.keys(previousPackagesByRoom.current))
    const currentRoomsIds = Object.keys(packagesByRoom)
    if (previousRoomsIds.size < currentRoomsIds.length) {
      const newRoomsIds = currentRoomsIds.filter(roomId => !previousRoomsIds.has(roomId))
      const roomsIdsWithoutPackage = newRoomsIds.filter(roomId => !packagesByRoom[roomId])

      if (roomsIdsWithoutPackage.length) {
        const roomsIndexes = roomsIdsWithoutPackage.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
        resultMessage.error = {
          title: `No available packages for ${arrayToStringListFormat(roomsIndexes, 'and')}`,
          message: 'Please revise number of guests or adjust selected dates.',
        }
      } else {
        if (Object.values(availablePackagesByRoom).some(pkgs => pkgs.length === 1)) {
          resultMessage.success = {
            title: 'Only one package available for your selected dates and guests',
          }
        } else {
          resultMessage.success = {
            title: `You have added ${pluralizeToString('room', newRoomsIds.length)} to your selection`,
          }
        }
      }
    }

    return resultMessage
  }, [packagesByRoom, previousPackagesByRoom, rooms, availablePackagesByRoom])

  useEffect(() => {
    const isCalendarsLoading = Object.values(isLoadingByRoom).some(Boolean) || Object.values(calendarsToBeFetchedByRoom).some(Boolean)
    if (isCalendarsLoading || activeFilter) return

    const changedRoomsFeedbackMessage = getChangedRoomsFeedbackMessage()
    const newRoomsFeedbackMessage = getNewRoomsFeedbackMessage()

    // Error messages has priority
    if (changedRoomsFeedbackMessage.error) {
      feedbackMessage.error(
        changedRoomsFeedbackMessage.error.title,
        changedRoomsFeedbackMessage.error.message,
      )
    } else if (newRoomsFeedbackMessage.error) {
      feedbackMessage.error(
        newRoomsFeedbackMessage.error.title,
        newRoomsFeedbackMessage.error.message,
      )
    } else if (changedRoomsFeedbackMessage.success) {
      feedbackMessage.success(
        changedRoomsFeedbackMessage.success.title,
        changedRoomsFeedbackMessage.success.message,
      )
    } else if (newRoomsFeedbackMessage.success) {
      feedbackMessage.success(
        newRoomsFeedbackMessage.success.title,
        newRoomsFeedbackMessage.success.message,
      )
    }

    Object.keys(packagesByRoom).forEach(roomId => {
      if (!(roomId in lastValidPackagesIdsByRoom.current) || packagesByRoom[roomId]) {
        lastValidPackagesIdsByRoom.current[roomId] = packagesByRoom[roomId]?.package.uniqueKey
      }
    })

    previousPackagesByRoom.current = packagesByRoom
    previousDuration.current = duration
    selectionAmountByPackage.current = countBy(packagesByRoom, (pkg) => pkg?.package.uniqueKey)
  }, [
    isLoadingByRoom, calendarsToBeFetchedByRoom, packagesByRoom, previousPackagesByRoom, feedbackMessage,
    activeFilter, duration, getNewRoomsFeedbackMessage, getChangedRoomsFeedbackMessage,
  ])

  const onBookNowClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!checkIn) {
      e.preventDefault()
      fireInteractionEvent(calendarAction(offer.analyticsType, CALENDAR_GA_EVENT_ACTION.OpenFilter, CALENDAR_GA_EVENT_LABEL.PackageOptionAvailability))

      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    } else if (!isLoggedIn) {
      e.preventDefault()
      dispatch(accountAccessShowLogin())

      loginListener.awaitResult().then(({ loggedIn }) => {
        if (loggedIn && !isRoomsInvalid(rooms)) {
          proceedToCheckout()
        }
      })
    } else if (!isRoomsInvalid(rooms)) {
      proceedToCheckout()
    }
  }, [checkIn, rooms, dispatch, offerPageDispatch, isLoggedIn, loginListener, offer.analyticsType, proceedToCheckout])

  const openOccupancyPicker = useCallback(() => {
    offerPageDispatch({
      type: OfferPageActions.toggleFilter,
      filter: 'capacity',
    })
  }, [offerPageDispatch])

  return (
    <PackageOptionsContainer ref={reviewPageRef}>
      <StyledHeaderContainer>
        <TitleSection>
          <Heading variant="heading3">Your {rooms.length} rooms</Heading>
          <BodyText variant="medium" colour="neutral-two">{occupantsString}</BodyText>
        </TitleSection>

        <FiltersSection>
          <BodyText variant="medium">Adjust all rooms:</BodyText>
          <StyledInlineGapContainer>
            <StyledFilterButton
              size="medium"
              disabled={buyNowBookLater || !isFlightsForAllRoomsAvailable}
              selected={isFlightsForAllRoomsSelected}
              onClick={onToggleFlightsForAllRooms}
            >
              {isFlightsForAllRoomsSelected ? 'Flights added' : 'Add flights'}
            </StyledFilterButton>
          </StyledInlineGapContainer>
        </FiltersSection>
      </StyledHeaderContainer>

      {rooms.map((room, index) => (
        <StyledPackageContainer key={index}>
          <StyledRoomPackageHeader>
            <div>
              <InlineContainer>
                <StyledRoomTitle variant="large" weight="bold">Room {index + 1} of {rooms.length}</StyledRoomTitle>
                <StyledRoomOccupancyString variant="medium" colour="neutral-two">({formatOccupantsShort(room)})</StyledRoomOccupancyString>
              </InlineContainer>
              <StyledRemoveButton kind="tertiary" data-idx={index} onClick={onRemoveRoom}>
                Remove
              </StyledRemoveButton>
            </div>
            {isCheapestPackagesActiveByRoom[room.roomId] &&
              <StatusLabel variant="confirmation">Best price selected</StatusLabel>
            }
          </StyledRoomPackageHeader>
          {!packagesByRoom[room.roomId] && (
            <NoPackagesContainer>
              <BodyText variant="large" weight="bold">No packages available</BodyText>
              <NoPackagesTextBox>
                {lastValidPackagesIdsByRoom.current[room.roomId] && (
                  <BodyText variant="medium">
                    <TextLink weight="regular" onClick={openOccupancyPicker}>Add another room</TextLink>{' '}
                    to accommodate your number of guests, or{' '}
                    <TextLink weight="regular" onClick={openOccupancyPicker}>Revise number of guests</TextLink>
                  </BodyText>
                )}
                {!lastValidPackagesIdsByRoom.current[room.roomId] && (
                  <TextLink weight="regular" onClick={openOccupancyPicker}>Please revise number of guests</TextLink>
                )}
              </NoPackagesTextBox>
            </NoPackagesContainer>
          )}
          {packagesByRoom[room.roomId] && (
            <MultipleRoomsBookingOption
              key={index}
              offer={offer}
              room={room}
              isFlightsEnabled={!buyNowBookLater && flightsInfoByRoom[room.roomId]?.isFlightsAvailable}
              packageOption={packagesByRoom[room.roomId]}
              packageOptionSelectionsAmount={selectionAmountByPackage.current.get(packagesByRoom[room.roomId].package.uniqueKey)}
              isFetching={isLoadingByRoom[room.roomId] || calendarsToBeFetchedByRoom[room.roomId]}
              isFlightsSelected={flightsInfoByRoom[room.roomId]?.isFlightsSelected}
              buyNowBookLater={buyNowBookLater}
              openRoomsModal={onOpenRoomsModal}
              isUpgradable={availablePackagesByRoom[room.roomId]?.length > 1}
              upgradeButtonText={isCheapestPackagesActiveByRoom[room.roomId] ? 'Upgrade room' : 'Change room'}
              availablePackagesQuantity={availablePackagesByRoom[room.roomId]?.length}
            />
          )}
        </StyledPackageContainer>
      ))}

      <FooterContainer>
        <RequestConnectingRoomsInput checked={closeProximity} onToggleConnectRooms={onToggleProximity} />
      </FooterContainer>

      <MultipleRoomsBookingContentFooter footerRef={footerRef} footerInView={footerInView} reviewPageInView={reviewPageInView}>
        {offer.hasBuyNowBookLater && (
          <Breakpoint min="desktop">
            <BNBLBox>
              <TextButton kind="tertiary" onClick={onOpenBnblModal}>
                Buy now choose dates later
              </TextButton>
              <Caption variant="medium" as="span">
                Travel until {moment(offer.travelToDate).format(DMY_CASUAL_FORMAT)}
              </Caption>
            </BNBLBox>
          </Breakpoint>
        )}

        <StyledActionFooterButtons>
          <div>
            <BodyText variant="small" colour="neutral-two" align="end">
              {rooms.length} rooms, {formatOccupantsAsGuests(allRooms)} {hasNoFlightPrices ? 'from' : ''}
            </BodyText>
            <StyledInlineGapContainer>
              <BodyText variant="large" weight="bold" align="end">
                <FormatCurrency value={totalRoomsPrice} format="roundedDollar" />
              </BodyText>
              <BodyText variant="medium"> total</BodyText>
            </StyledInlineGapContainer>
          </div>
          <StyledOfferBookButton kind="primary" type="submit" disabled={isBookNowBtnDisabled} onClick={onBookNowClick}>
            {checkIn ? `Book ${rooms.length} rooms now` : 'Check availability'}
          </StyledOfferBookButton>
        </StyledActionFooterButtons>

        {offer.hasBuyNowBookLater && footerInView && (
          <Breakpoint max="tablet">
            <BNBLBox>
              <TextButton kind="tertiary" onClick={onOpenBnblModal}>
                Buy now choose dates later
              </TextButton>
              <Caption variant="medium" as="span">
                Travel until {moment(offer.travelToDate).format(DMY_CASUAL_FORMAT)}
              </Caption>
            </BNBLBox>
          </Breakpoint>
        )}
      </MultipleRoomsBookingContentFooter>

      {buyNowBookLater && <BnblModal closeBnblModal={closeBNBLModal} isOpen={buyNowBookLater} offer={offer}/>}
      <MultipleRoomsBookingRoomsModal
        isOpen={isOpenRoomsModal}
        room={changedRoom}
        title={`Upgrade package for room ${changedRoomIndex + 1} of ${rooms.length}`}
        subtitle={`${changedRoom ? availablePackagesByRoom[changedRoom.roomId].length : 0} packages available`}
        availablePackages={changedRoom ? availablePackagesByRoom[changedRoom.roomId] : []}
        selectedPackageOption={changedRoom && packagesByRoom[changedRoom.roomId]}
        closeModal={closeRoomsModal}
        onSelect={onSelectPackage}
      />
    </PackageOptionsContainer>
  )
}

MultipleRoomsBookingContent.defaultProps = {
  rooms: [],
}

export default withOfferPageAndReduxState((state, appState) => ({
  currencyCode: appState.geo.currentCurrency,
  checkIn: state.checkInDate,
  duration: state.duration,
  rooms: state.rooms,
  offer: state.offer,
  isMultiRoomConnected: state.isMultiRoomConnected,
  activeFilter: state.activeFilter,
  account: appState.auth.account,
  cheapestPackagesByRoom: getCheapestPackagesByRoom(state, appState),
  activePackagesByRoom: getActivePackagesByRoom(state, appState),
  isLoadingByRoom: isLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  flightsInfoByRoom: getFlightsInfoByRoom(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  durationPackagesByRoom: getDurationPackageOptionsByRoom(state, appState),
  fallbackPackagesByRoom: getDurationPackageOptionsByRoom(state, appState),
  isLoggedIn: isLoggedInSelector(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  airport: state.airport,
  roomTypesByRoom: mergeObjects(state.multipleRoomPackages.map(mrpkg => ({ [mrpkg.roomId]: mrpkg.roomTypeId }))),
}))(MultipleRoomsBookingContent)
